<h2 mat-dialog-title>{{ isEditMode() ? 'Edytuj wpis' : 'Nowy wpis' }}</h2>

<mat-dialog-content>
	<form [formGroup]="form" class="expense-form">
		<mat-button-toggle-group formControlName="type" class="type-toggle">
			<mat-button-toggle value="expense">Zwrot (−)</mat-button-toggle>
			<mat-button-toggle value="income">Przychód (+)</mat-button-toggle>
		</mat-button-toggle-group>

		<div class="form-row">
			<mat-form-field appearance="outline" class="form-field">
				<mat-label>Data</mat-label>
				<input matInput [matDatepicker]="datePicker" [max]="maxDate()" formControlName="date" />
				<mat-datepicker-toggle matIconSuffix [for]="datePicker"></mat-datepicker-toggle>
				<mat-datepicker #datePicker></mat-datepicker>
				@if (form.get('date')?.hasError('required') && form.get('date')?.touched) {
					<mat-error>Data jest wymagana</mat-error>
				}
				@if (form.get('date')?.hasError('matDatepickerMax')) {
					<mat-error>Nie można wybrać daty z przyszłości</mat-error>
				}
			</mat-form-field>

			<mat-form-field appearance="outline" floatLabel="always" class="form-field">
				<mat-label>Kwota</mat-label>
				<input matInput type="number" formControlName="amount" min="0.01" step="0.01" />
				@if (form.get('amount')?.hasError('required') && form.get('amount')?.touched) {
					<mat-error>Kwota jest wymagana</mat-error>
				}
				@if (form.get('amount')?.hasError('min') && form.get('amount')?.touched) {
					<mat-error>Kwota musi być większa od 0</mat-error>
				}
			</mat-form-field>

			@if (showCommission()) {
				<mat-form-field appearance="outline" floatLabel="always" class="form-field">
					<mat-label>Prowizja</mat-label>
					<input matInput type="number" formControlName="commission" min="0" step="0.01" />
					@if (form.get('commission')?.hasError('min') && form.get('commission')?.touched) {
						<mat-error>Prowizja nie może być ujemna</mat-error>
					}
				</mat-form-field>
			}
		</div>

		<div class="form-row">
			<mat-form-field appearance="outline" class="form-field">
				<mat-label>Waluta</mat-label>
				<mat-select formControlName="currency">
					@for (curr of currencies; track curr.value) {
						<mat-option [value]="curr.value">{{ curr.label }}</mat-option>
					}
				</mat-select>
			</mat-form-field>

			@if (showTargetCurrency()) {
				<mat-form-field appearance="outline" class="form-field">
					<mat-label>Waluta rozliczenia</mat-label>
					<mat-select formControlName="targetCurrency">
						<mat-option value="PLN">PLN</mat-option>
						<mat-option value="EUR">EUR</mat-option>
					</mat-select>
				</mat-form-field>
			}
		</div>

		<mat-form-field appearance="outline" class="form-field-full">
			<mat-label>Status</mat-label>
			<mat-select formControlName="status">
				@for (s of statuses; track s) {
					<mat-option [value]="s">{{ statusLabel[s] }}</mat-option>
				}
			</mat-select>
		</mat-form-field>

		<div class="lines-section">
			<div class="lines-header">
				<h3>Pozycje</h3>
				<span
					class="sum-indicator"
					[class.sum-ok]="sumMatches()"
					[class.sum-mismatch]="!sumMatches() && linesTargetPln() > 0"
					[matTooltip]="
						sumMatches()
							? 'Suma pozycji zgadza się z kwotą do rozbicia (kwota − prowizja)'
							: 'Suma pozycji: ' + linesSumPln() + ' / Do rozbicia: ' + linesTargetPln()
					"
				>
					{{ linesSumPln() | number: '1.2-2' }} /
					{{ linesTargetPln() | number: '1.2-2' }}
					@if (sumDifference() !== 0) {
						<span class="diff">
							({{ sumDifference() > 0 ? 'brakuje' : 'nadwyżka' }}:
							{{ sumDifference() | number: '1.2-2' }})
						</span>
					}
				</span>
			</div>

			<div formArrayName="lines" class="lines-array">
				@for (line of lines.controls; track $index; let i = $index) {
					<div [formGroupName]="i" class="line-row">
						<mat-form-field appearance="outline" class="line-category">
							<mat-label>Kategoria</mat-label>
							<input
								matInput
								formControlName="category"
								[matAutocomplete]="categoryAuto"
								placeholder="Wyszukaj..."
							/>
							<mat-autocomplete #categoryAuto="matAutocomplete" [displayWith]="displayCategory">
								@for (
									category of categories() | categoryFilter: line.get('category')?.value;
									track category.id
								) {
									<mat-option [value]="category">{{ category.name }}</mat-option>
								}
							</mat-autocomplete>
						</mat-form-field>

						<mat-form-field appearance="outline" floatLabel="always" class="line-amount">
							<mat-label>Kwota</mat-label>
							<input matInput type="number" formControlName="amount" min="0.01" step="0.01" />
							@if (line.get('amount')?.hasError('required') && line.get('amount')?.touched) {
								<mat-error>Wymagane</mat-error>
							}
							@if (line.get('amount')?.hasError('min') && line.get('amount')?.touched) {
								<mat-error>> 0</mat-error>
							}
						</mat-form-field>

						<mat-form-field appearance="outline" class="line-description">
							<mat-label>Opis pozycji</mat-label>
							<input matInput formControlName="description" maxlength="500" />
						</mat-form-field>

						<button
							mat-icon-button
							type="button"
							class="line-action"
							matTooltip="Wypełnij resztą"
							(click)="fillRemainderOnLine(i)"
						>
							<i class="fas fa-equals" aria-hidden="true"></i>
						</button>

						<button
							mat-icon-button
							type="button"
							class="line-action"
							color="warn"
							[disabled]="lines.length <= 1"
							matTooltip="Usuń pozycję"
							(click)="removeLine(i)"
						>
							<i class="fas fa-trash" aria-hidden="true"></i>
						</button>
					</div>
				}
			</div>

			<button mat-stroked-button type="button" class="add-line-button" (click)="addLine()">
				<i class="fas fa-plus" aria-hidden="true"></i>
				Dodaj pozycję
			</button>

			@if (form.hasError('sumMismatch') && (form.touched || form.dirty)) {
				<div class="sum-error">
					Suma pozycji ({{ form.errors?.['sumMismatch'].linesSum | number: '1.2-2' }}) nie zgadza się z kwotą
					do rozbicia ({{ form.errors?.['sumMismatch'].targetAmount | number: '1.2-2' }} = kwota − prowizja).
					{{ form.errors?.['sumMismatch'].diff > 0 ? 'Brakuje' : 'Nadwyżka' }}:
					{{ form.errors?.['sumMismatch'].diff | number: '1.2-2' }}.
				</div>
			}
		</div>

		<div class="section-divider">Opcjonalnie</div>

		<mat-form-field appearance="outline" class="form-field-full">
			<mat-label>Pojazd</mat-label>
			<input
				matInput
				formControlName="vehicle"
				[matAutocomplete]="vehicleAuto"
				placeholder="Wyszukaj pojazd..."
			/>
			<mat-autocomplete #vehicleAuto="matAutocomplete" [displayWith]="displayVehicle">
				@for (vehicle of vehicleOptions(); track vehicle.id) {
					<mat-option [value]="vehicle"> {{ vehicle.name }} ({{ vehicle.registrationNumber }}) </mat-option>
				}
			</mat-autocomplete>
			@if (vehicleControl.value) {
				<button
					matSuffix
					mat-icon-button
					type="button"
					class="clear-vehicle-button"
					aria-label="Wyczyść pojazd"
					(click)="vehicleControl.setValue(null); $event.stopPropagation()"
				>
					<i class="fas fa-times" aria-hidden="true"></i>
				</button>
			}
		</mat-form-field>

		<mat-form-field appearance="outline" class="form-field-full">
			<mat-label>Numer dokumentu</mat-label>
			<input matInput formControlName="documentNumber" maxlength="100" />
			@if (form.get('documentNumber')?.hasError('maxlength') && form.get('documentNumber')?.touched) {
				<mat-error>Maksymalnie 100 znaków</mat-error>
			}
		</mat-form-field>

		<mat-form-field appearance="outline" class="form-field-full">
			<mat-label>Data płatności</mat-label>
			<input
				matInput
				[matDatepicker]="paymentDatePicker"
				[max]="maxPaymentDate()"
				formControlName="paymentDate"
			/>
			<mat-datepicker-toggle matIconSuffix [for]="paymentDatePicker"></mat-datepicker-toggle>
			<mat-datepicker #paymentDatePicker></mat-datepicker>
			@if (form.get('paymentDate')?.hasError('matDatepickerMax')) {
				<mat-error>Nie można wybrać daty z przyszłości</mat-error>
			}
		</mat-form-field>

		<mat-form-field appearance="outline" class="form-field-full">
			<mat-label>Opis (nagłówek)</mat-label>
			<textarea matInput formControlName="description" maxlength="500" rows="4"></textarea>
		</mat-form-field>

		@if (gboxScanId() !== null) {
			<a
				mat-stroked-button
				type="button"
				class="gbox-report-link"
				[routerLink]="['/tms/reports']"
				[queryParams]="{ scanId: gboxScanId() }"
				target="_blank"
				rel="noopener"
				matTooltip="Otwórz raport GBox powiązany z tym wpisem w nowej karcie"
			>
				<i class="fas fa-up-right-from-square" aria-hidden="true"></i>
				Otwórz raport GBox
			</a>
		}

		<mat-checkbox formControlName="hasOriginalDocument" class="original-document-checkbox">
			Posiadamy oryginał dokumentu
		</mat-checkbox>
	</form>
</mat-dialog-content>

<mat-dialog-actions align="end">
	<button mat-stroked-button (click)="onCancel()">Anuluj</button>
	<button mat-flat-button color="primary" (click)="onSave()" [disabled]="form.invalid">
		{{ isEditMode() ? 'Zapisz' : 'Dodaj' }}
	</button>
</mat-dialog-actions>
